import { useContext, createContext, useState, useEffect } from "react";
import { useQuery } from "../Utils/query";
import { supabasePublic } from "../config/supabaseClient";
import { useAuth } from "./useAuth";
import { isAbortError } from "../Utils/errors/isAbortError";
import { pickBestDomainMapping } from "../Utils/CustomDomain/pickBestDomainMapping";

// Create a context for custom domain
const CustomDomainContext = createContext<{
  domainData: any;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
  customDomain: string | null;
  // domainStatus: string | null;
}>({
  domainData: null,
  isLoading: true,
  isFetching: false,
  refetch: () => {},
  customDomain: null,
        // domainStatus: null,
});

// Delay enabling the query so it doesn't run in the same tick as login (avoids abort race)
const DOMAIN_QUERY_DELAY_MS = 500;

export const CustomDomainProvider = ({ children }: any) => {
  const { username } = useAuth();
  const [allowDomainFetch, setAllowDomainFetch] = useState(false);

  useEffect(() => {
    if (!username) {
      setAllowDomainFetch(false);
      return;
    }
    const timer = setTimeout(() => setAllowDomainFetch(true), DOMAIN_QUERY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [username]);

  const {
    isLoading,
    isFetching,
    data: domainData,
    refetch,
  } = useQuery(
    ["domainStatus", username],
    async () => {
      if (!username) return null;
      try {
        // Public/session-free client: avoids AbortError races with login refresh.
        // Do not use maybeSingle — apex + www active rows throw PGRST116.
        const { data, error }: any = await supabasePublic
          .from("domain_mappings")
          // Table has created_at only — selecting updated_at returns 400 / 42703.
          .select("domain,status,is_active,created_at")
          .eq("slug", username)
          .eq("status", "active")
          .limit(20);
        if (error && !isAbortError(error)) {
          console.error("Error in useCustomDomain:", error);
          return null;
        }
        const best = pickBestDomainMapping(Array.isArray(data) ? data : []);
        if (best) return best;
        return null;
      } catch (err) {
        // Login/navigation often unmounts before this query finishes; treat abort as cancelled, not error
        if (isAbortError(err)) {
          return null;
        }
        console.error("Error in useCustomDomain:", err);
        return null;
      }
    },
    {
      enabled: !!username && allowDomainFetch,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }
  );

  const value = {
    domainData: domainData || null,
    isLoading,
    isFetching,
    refetch,
    customDomain: domainData?.domain || null,
    // domainStatus: domainData?.status || null,
  };

  return (
    <CustomDomainContext.Provider value={value}>
      {children}
    </CustomDomainContext.Provider>
  );
};

// Export the useCustomDomain hook
export const useCustomDomain = () => {
  return useContext(CustomDomainContext);
};

